<template>
  <div class="content-wrapper-inner p-3">
    <!-- Header -->
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title" style="font-weight: 700; color: #2c3e50;">
          <i class="fa fa-building text-primary"></i> Reporte de Cantidad de Personal por Empresa (Global)
        </h3>
        <p class="text-muted" style="margin: 4px 0 0 0; font-size: 13px;">
          Consolidado multiplataforma de empleados registrados por empresa con filtros mensuales y por estado laboral.
        </p>
      </div>

      <div class="box-body">
        <!-- Filtros de Consulta -->
        <el-form :inline="true" size="small" class="demo-form-inline">
          <el-form-item label="Empresa">
            <el-select
              v-model="selectedCompany"
              style="width: 220px;"
              placeholder="Todas las Empresas"
              filterable
              @change="fetchReport"
            >
              <el-option
                v-for="c in availableCompanies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              >
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Mes / Año">
            <el-date-picker
              v-model="month"
              type="month"
              format="yyyy-MM"
              value-format="yyyy-MM"
              placeholder="Todos los meses"
              clearable
              style="width: 170px;"
              @change="fetchReport"
            >
            </el-date-picker>
          </el-form-item>

          <el-form-item label="Estado">
            <el-select v-model="status" style="width: 180px;" @change="fetchReport">
              <el-option label="Todos (Activos e Inactivos)" value="ALL"></el-option>
              <el-option label="Solo Activos (ACT)" value="ACT"></el-option>
              <el-option label="Solo Inactivos (INA)" value="INA"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Filtrar">
            <el-input
              v-model="searchQuery"
              placeholder="Buscar por área o nombre..."
              clearable
              prefix-icon="el-icon-search"
              style="width: 200px;"
            >
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="fetchReport" :loading="loading">
              Consultar
            </el-button>
            <el-button type="success" icon="el-icon-download" @click="exportExcel" :disabled="filteredCompanies.length === 0">
              Exportar a Excel
            </el-button>
          </el-form-item>
        </el-form>

        <!-- Tarjetas de Métricas Consolidadas -->
        <div class="row margin-top" v-if="summary">
          <div class="col-md-2 col-sm-4 col-xs-12" style="width: 20%;">
            <div class="info-box bg-blue">
              <span class="info-box-icon"><i class="fa fa-building-o"></i></span>
              <div class="info-box-content">
                <span class="info-box-text">Empresas</span>
                <span class="info-box-number">{{ summary.total_companies }}</span>
                <span class="progress-description">Bases de Datos</span>
              </div>
            </div>
          </div>

          <div class="col-md-2 col-sm-4 col-xs-12" style="width: 20%;">
            <div class="info-box bg-purple">
              <span class="info-box-icon"><i class="fa fa-users"></i></span>
              <div class="info-box-content">
                <span class="info-box-text">Total Personal</span>
                <span class="info-box-number">{{ summary.total_employees }}</span>
                <span class="progress-description">En el período {{ month || 'Histórico' }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-2 col-sm-4 col-xs-12" style="width: 20%;">
            <div class="info-box bg-green">
              <span class="info-box-icon"><i class="fa fa-user-check"></i></span>
              <div class="info-box-content">
                <span class="info-box-text">Activos (ACT)</span>
                <span class="info-box-number">{{ summary.total_active }}</span>
                <span class="progress-description">{{ getPercentage(summary.total_active, summary.total_employees) }}% del Total</span>
              </div>
            </div>
          </div>

          <div class="col-md-2 col-sm-4 col-xs-12" style="width: 20%;">
            <div class="info-box bg-red">
              <span class="info-box-icon"><i class="fa fa-user-times"></i></span>
              <div class="info-box-content">
                <span class="info-box-text">Inactivos (INA)</span>
                <span class="info-box-number">{{ summary.total_inactive }}</span>
                <span class="progress-description">{{ getPercentage(summary.total_inactive, summary.total_employees) }}% del Total</span>
              </div>
            </div>
          </div>

          <div class="col-md-2 col-sm-4 col-xs-12" style="width: 20%;">
            <div class="info-box bg-yellow">
              <span class="info-box-icon"><i class="fa fa-clock-o"></i></span>
              <div class="info-box-content">
                <span class="info-box-text">Con Marcaciones</span>
                <span class="info-box-number">{{ summary.total_with_records || 0 }}</span>
                <span class="progress-description">Con Registros en Mes</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabla Detallada por Empresa -->
        <div class="margin-top" v-loading="loading">
          <el-table
            :data="filteredCompanies"
            stripe
            border
            style="width: 100%"
            size="medium"
          >
            <el-table-column label="Empresa / Razón Social" min-width="180">
              <template slot-scope="scope">
                <span class="company-badge bg-navy">
                  <i class="fa fa-building"></i> {{ scope.row.company_name }}
                </span>
              </template>
            </el-table-column>

            <el-table-column prop="db_name" label="Base de Datos" width="150" align="center">
              <template slot-scope="scope">
                <code style="color: #2980b9;">{{ scope.row.db_name }}</code>
              </template>
            </el-table-column>

            <el-table-column prop="total_employees" label="Total Personal" width="130" align="center" sortable>
              <template slot-scope="scope">
                <strong style="font-size: 16px; color: #2c3e50;">{{ scope.row.total_employees }}</strong>
              </template>
            </el-table-column>

            <el-table-column prop="active_employees" label="Activos (ACT)" width="130" align="center" sortable>
              <template slot-scope="scope">
                <span class="label label-success" style="font-size: 13px; padding: 6px 10px;">
                  <i class="fa fa-check"></i> {{ scope.row.active_employees }}
                </span>
              </template>
            </el-table-column>

            <el-table-column prop="inactive_employees" label="Inactivos (INA)" width="130" align="center" sortable>
              <template slot-scope="scope">
                <span class="label label-danger" style="font-size: 13px; padding: 6px 10px;">
                  <i class="fa fa-times"></i> {{ scope.row.inactive_employees }}
                </span>
              </template>
            </el-table-column>

            <el-table-column prop="employees_with_records" label="Con Marcaciones" width="150" align="center" sortable>
              <template slot-scope="scope">
                <span class="label label-warning" style="font-size: 13px; padding: 6px 10px; background-color: #f39c12 !important; color: #fff;">
                  <i class="fa fa-clock-o"></i> {{ scope.row.employees_with_records }}
                </span>
              </template>
            </el-table-column>

            <el-table-column label="Desglose por Área Funcional" min-width="240">
              <template slot-scope="scope">
                <el-popover
                  placement="left"
                  title="Desglose por Áreas"
                  width="320"
                  trigger="hover"
                >
                  <table class="table table-condensed table-striped" style="margin-bottom: 0;">
                    <thead>
                      <tr>
                        <th>Área Funcional</th>
                        <th style="text-align: right;">Personal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(area, idx) in scope.row.areas" :key="idx">
                        <td>{{ area.name }}</td>
                        <td style="text-align: right; font-weight: bold;">{{ area.count }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <el-button slot="reference" size="mini" type="info" plain icon="el-icon-document">
                    Ver {{ scope.row.areas ? scope.row.areas.length : 0 }} Áreas
                  </el-button>
                </el-popover>
              </template>
            </el-table-column>
          </el-table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GlobalHeadcountReport",
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    return {
      selectedCompany: "ALL",
      month: `${yyyy}-${mm}`,
      status: "ALL",
      searchQuery: "",
      loading: false,
      availableCompanies: [
        { id: "ALL", name: "Todas las Empresas" }
      ],
      summary: {
        total_companies: 0,
        total_employees: 0,
        total_active: 0,
        total_inactive: 0,
        total_with_records: 0,
      },
      companies: [],
    };
  },
  computed: {
    filteredCompanies() {
      if (!this.searchQuery) return this.companies;
      const query = this.searchQuery.toLowerCase().trim();
      return this.companies.filter(
        (c) =>
          c.company_name.toLowerCase().includes(query) ||
          c.db_name.toLowerCase().includes(query) ||
          (c.areas && c.areas.some(a => a.name.toLowerCase().includes(query)))
      );
    },
  },
  mounted() {
    this.fetchReport();
  },
  methods: {
    fetchReport() {
      this.loading = true;
      axios
        .get("/api/reports/globalheadcount", {
          params: {
            company: this.selectedCompany,
            month: this.month,
            status: this.status,
          },
        })
        .then((response) => {
          if (response.data.available_companies && response.data.available_companies.length > 1) {
            this.availableCompanies = response.data.available_companies;
          }
          this.summary = response.data.summary || {};
          this.companies = response.data.companies || [];
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$message.error(
            "Error al consultar el reporte de personal global."
          );
        });
    },
    getPercentage(part, total) {
      if (!total || total === 0) return 0;
      return ((part / total) * 100).toFixed(1);
    },
    exportExcel() {
      let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
      csvContent += "REPORTE GLOBAL DE PERSONAL POR EMPRESA\n";
      csvContent += `EMPRESA FILTRO: ${this.selectedCompany}\n`;
      csvContent += `PERIODO: ${this.month || 'Todos'}\n`;
      csvContent += `FILTRO ESTADO: ${this.status}\n\n`;
      csvContent += "Empresa,Base de Datos,Total Personal,Personal Activo,Personal Inactivo,Con Marcaciones\n";

      this.filteredCompanies.forEach((c) => {
        csvContent += `"${c.company_name}","${c.db_name}",${c.total_employees},${c.active_employees},${c.inactive_employees},${c.employees_with_records || 0}\n`;
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Reporte_Personal_Global_${this.month || 'Todos'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.company-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 4px;
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.5px;
}

.bg-navy {
  background-color: #001f3f !important;
}

.margin-top {
  margin-top: 15px;
}

.p-3 {
  padding: 15px;
}
</style>
